import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { useUsers } from '@/hooks/use-users';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, UserMinus, UserPlus, Users, Edit, Trash2 } from 'lucide-react';

const STATUS_STYLES: Record<
  'active' | 'blocked' | 'unknown',
  { label: string; className: string }
> = {
  active: {
    label: 'Hoạt động',
    className: 'bg-green-100 text-green-800',
  },
  blocked: {
    label: 'Bị khóa',
    className: 'bg-red-100 text-red-800',
  },
  unknown: {
    label: 'Không xác định',
    className: 'bg-gray-100 text-gray-800',
  },
};

const ROLE_LABELS: Record<string, string> = {
  seeker: 'Người dùng',
  partner: 'Đối tác',
  admin: 'Quản trị',
};

export const Route = createFileRoute('/users/')({
  component: UsersListPage,
});

function UsersListPage() {
  const { data: users = [], isLoading, error } = useUsers();
  const [search, setSearch] = useState('');

  const { filteredUsers, activeCount, blockedCount } = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    const filtered = users.filter((user) =>
      normalizedSearch.length === 0
        ? true
        : (user.email ?? '').toLowerCase().includes(normalizedSearch)
    );
    return {
      filteredUsers: filtered,
      activeCount: users.filter((user) => user.status === 'active').length,
      blockedCount: users.filter((user) => user.status === 'blocked').length,
    };
  }, [users, search]);

  const totalCount = users.length;

  return (
    <div className="space-y-6">
      {/* Filters Section */}
      <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
        <div className="flex items-center space-x-6">
          <button
            className="flex items-center space-x-2 font-medium text-primary"
          >
            <span className="text-sm">All</span>
            <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">
              {totalCount}
            </span>
          </button>
          <div className="w-px h-4 bg-gray-200"></div>
          <button
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
          >
            <span className="text-sm">Active</span>
            <span className="bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full">
              {activeCount}
            </span>
          </button>
          <div className="w-px h-4 bg-gray-200"></div>
          <button
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
          >
            <span className="text-sm">Blocked</span>
            <span className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full">
              {blockedCount}
            </span>
          </button>
        </div>
      </div>

    {/* Search Bar */}
      <div className="flex items-center space-x-3">
        <div className="flex-1 relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by email..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <Button variant="outline" className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
          <Search className="h-4 w-4 text-gray-500" />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-gray-500">Loading users...</p>
        </div>
      ) : error ? (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-red-600 font-medium">Error: {error.message}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-1">
                    <span>Email</span>
                  </div>
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const statusKey: 'active' | 'blocked' | 'unknown' =
                  user.status === 'active' ? 'active' : user.status === 'blocked' ? 'blocked' : 'unknown';
                const statusInfo = STATUS_STYLES[statusKey];

                return (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.email ?? 'Unknown email'}</div>
                        <div className="text-sm text-gray-500">ID: {user.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        {ROLE_LABELS[user.role ?? ''] ?? 'Not set'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block text-xs px-2 py-1 rounded-full ${statusInfo.className}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="icon" className="text-gray-400 hover:text-gray-600">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-gray-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                    No users found matching the current search.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
